import { shopifyGidSchema, ShopifyIdType } from "@redotech/shopify-util/id";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { TaxonomyCategorySchema } from "../common-schemas";
import { ShopifyMetafieldSchema } from "./common";
import {
  ShopifyListingMediaSchema,
  ShopifyListingMediaWithoutRedoIds,
} from "./shopify-media";
import {
  ShopifyListingPublicationSchema,
  ShopifyListingPublicationWithoutRedoIds,
} from "./shopify-publication";
import {
  ShopifyListingVariantSchema,
  ShopifyVariantListingWithoutRedoIds,
} from "./shopify-variant";

export enum ShopifyProductStatusEnum {
  ACTIVE = "ACTIVE",
  ARCHIVED = "ARCHIVED",
  DRAFT = "DRAFT",
}
export const ShopifyProductStatusSchema = createEnum(ShopifyProductStatusEnum);

const ShopifyListingOptionSchema_ = z.object({
  id: z.string(),
  name: z.string(),
  position: z.number(),
  values: z.array(z.string()),
});
export const ShopifyListingOptionSchema: z.ZodType<ShopifyListingOption> =
  ShopifyListingOptionSchema_;
export interface ShopifyListingOption
  extends z.infer<typeof ShopifyListingOptionSchema_> {}

const ShopifySeoSchema_ = z.object({
  title: z.string().nullable(),
  description: z.string().nullable(),
});
export const ShopifySeoSchema: z.ZodType<ShopifySeo> = ShopifySeoSchema_;
export interface ShopifySeo extends z.infer<typeof ShopifySeoSchema_> {}

export const ShopifyListingSchema_ = z.object({
  channel: z.literal("SHOPIFY"),
  id: z.string(),
  shopifyId: shopifyGidSchema(ShopifyIdType.Product).nullable(),
  title: z.string(),
  descriptionHtml: z.string(),
  handle: z.string(),
  productType: z.string(),
  category: TaxonomyCategorySchema.nullable(),
  vendor: z.string(),
  tags: z.array(z.string()),
  options: z.array(ShopifyListingOptionSchema),
  seo: ShopifySeoSchema,
  status: ShopifyProductStatusSchema,
  onlineStoreUrl: z.string().nullable(),
  onlineStorePreviewUrl: z.string().nullable(),
  tracksInventory: z.boolean(),
  metafields: z.array(ShopifyMetafieldSchema),
  variants: z.array(ShopifyListingVariantSchema),
  media: z.array(ShopifyListingMediaSchema),
  publications: z.array(ShopifyListingPublicationSchema),
  // collections: z.array(ShopifyCollectionSchema), // We aren't using collections yet.
  createdAt: z.date(),
  updatedAt: z.date(),
  publishedAt: z.date().nullable(),
});
export const ShopifyListingSchema: z.ZodType<ShopifyListing> =
  ShopifyListingSchema_;
export interface ShopifyListing extends z.infer<typeof ShopifyListingSchema_> {}

export type ShopifyListingWithoutRedoIds = Omit<
  ShopifyListing,
  "id" | "variants" | "media" | "publications" // | "collections" // We aren't using collections yet.
> & {
  variants: ShopifyVariantListingWithoutRedoIds[];
  media: ShopifyListingMediaWithoutRedoIds[];
  publications: ShopifyListingPublicationWithoutRedoIds[];
  // collections: ShopifyCollectionWithoutRedoIds[]; // We aren't using collections yet.
};
